/*
 * The dashboard is FRENCH — Carrefour France is the only audience, so every
 * label and every figure follows French usage, not the English design comp:
 * a non-breaking space as the thousands separator and before a unit or a
 * percent sign, and a comma for decimals.
 */
export const NBSP = '\u00a0';

/** Tabs, in header order. The ids stay English: they are the `?tab=` URL keys, not labels. */
export const MONOPOLY_TABS = [
  { id: 'overview', label: "Vue d'ensemble" },
  { id: 'participants', label: 'Participants et opt-ins' },
  { id: 'visits', label: 'Visites et sessions' },
  { id: 'cards', label: 'Cartes' },
  { id: 'prizes', label: 'Collections et lots' },
  { id: 'compare', label: '2026 vs 2025' },
] as const;

export type MonopolyTab = (typeof MONOPOLY_TABS)[number]['id'];

export function isMonopolyTab(v: string | null): v is MonopolyTab {
  return v !== null && MONOPOLY_TABS.some((t) => t.id === v);
}

/** Every kind of space Intl may emit becomes the non-breaking one, so a number never wraps. */
const nb = (s: string) => s.replace(/[\u202f\u00a0\s]/g, NBSP);

/** 15341880 → "15 341 880". */
export const fmtInt = (n: number) => nb(Math.round(n).toLocaleString('fr-FR'));

/** 61.4 → "61,4 %"; whole numbers stay whole ("44 %"). */
export const fmtPct = (pct: number, digits = 1) => `${nb(Number(pct.toFixed(digits)).toLocaleString('fr-FR'))}${NBSP}%`;

/** 3.18 → "3,18", 10.2 → "10,2" — no trailing zeros. */
export const fmtDecimal = (n: number, max = 2) => nb(n.toLocaleString('fr-FR', { maximumFractionDigits: max }));

/** Axis labels: 2000000 → "2,0 M", 300000 → "300 k", 0 → "0". */
export function fmtCompact(n: number): string {
  if (n === 0) return '0';
  if (Math.abs(n) >= 1_000_000) return `${nb((n / 1_000_000).toFixed(1).replace('.', ','))}${NBSP}M`;
  if (Math.abs(n) >= 1_000) return `${fmtInt(n / 1_000)}${NBSP}k`;
  return fmtInt(n);
}

/**
 * Axis maximum that fits the data: the smallest "nice" number (1, 2, 4, 5, 8
 * or 10 × a power of ten) at or above the largest value, so four gridlines land
 * on round ticks. The design drew fixed axes sized for its sample (300k, 2.0M);
 * real data is relative — a launch-day 121k must not sit under a 300k ceiling,
 * nor 12 cards under one. An all-zero series gets a floor of 10.
 */
export function niceMax(values: number[]): number {
  const peak = Math.max(0, ...values.filter((v) => Number.isFinite(v)));
  if (peak <= 0) return 10;
  const exp = Math.floor(Math.log10(peak));
  const base = 10 ** exp;
  for (const m of [1, 2, 4, 5, 8, 10]) {
    if (m * base >= peak) return m * base;
  }
  return 10 * base;
}

/**
 * Bar value labels: 268140 → "268 k". Below a thousand the number is shown as
 * it is — a launch day of 131 participants must not read "0 k".
 */
export const fmtK = (n: number) => (Math.abs(n) < 1_000 ? fmtInt(n) : `${fmtInt(n / 1_000)}${NBSP}k`);

const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/** '2026-09-08' → "08 sept." (fixed abbreviations, so no ICU version can shift them); short → "08". */
export function fmtDay(iso: string, style: 'short' | 'long' = 'long'): string {
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return style === 'short' ? dd : `${dd} ${MONTHS_FR[d.getUTCMonth()]}`;
}

/** ISO timestamp → "14:32:05" in the viewer's local time, or "—". */
export function fmtClock(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** Safe share: 0 when the denominator is 0. */
export const share = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0);
